<template>
  <v-container fluid class="kv-confirmation-container">
    <v-row>
      <v-col cols="12">
        <!-- Header -->
        <v-card class="header-card mb-4">
          <v-card-title class="header-title">
            <div class="d-flex align-center">
              <v-icon color="success" :size="isMobile ? 24 : 32" class="mr-2">mdi-file-document-check</v-icon>
              <span class="title-text">KV-Bestätigungen</span>
            </div>

            <!-- Statistik-Chips und Buttons in einer Zeile -->
            <div class="header-right">
              <!-- Statistik-Chips -->
              <div class="stats-chips" v-if="!isMobile">
                <v-chip
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-file-document"
                >
                  Gesamt: {{ stats.total || 0 }}
                </v-chip>

                <v-chip
                  v-if="stats.pending > 0"
                  color="warning"
                  variant="elevated"
                  prepend-icon="mdi-clock-alert"
                >
                  Offen: {{ stats.pending }}
                </v-chip>

                <v-chip
                  v-if="stats.completed > 0"
                  color="success"
                  variant="outlined"
                  prepend-icon="mdi-check-circle"
                >
                  Erledigt: {{ stats.completed }}
                </v-chip>
              </div>

              <!-- Action Buttons -->
              <div class="header-actions">
                <!-- Neue KV-Bestätigung Button (nur für Admins/Super-Admins/Entwickler) -->
                <v-btn
                  v-if="canCreateKv"
                  color="success"
                  :prepend-icon="isMobile ? undefined : 'mdi-plus'"
                  :icon="isMobile ? 'mdi-plus' : undefined"
                  :size="isMobile ? 'small' : 'default'"
                  @click="showNewKvDialog = true"
                >
                  <span v-if="!isMobile">Neue KV</span>
                </v-btn>

                <!-- Aktualisieren Button -->
                <v-btn
                  icon="mdi-refresh"
                  variant="text"
                  :size="isMobile ? 'small' : 'default'"
                  @click="loadKvConfirmations"
                  :loading="loading"
                />
              </div>
            </div>
          </v-card-title>

          <!-- Statistik-Chips - nur auf Mobile in zweiter Zeile -->
          <v-card-subtitle v-if="isMobile" class="stats-row pa-3 pt-0">
            <v-chip
              color="primary"
              variant="outlined"
              prepend-icon="mdi-file-document"
              size="small"
            >
              Gesamt: {{ stats.total || 0 }}
            </v-chip>

            <v-chip
              v-if="stats.pending > 0"
              color="warning"
              variant="elevated"
              prepend-icon="mdi-clock-alert"
              size="small"
            >
              Offen: {{ stats.pending }}
            </v-chip>

            <v-chip
              v-if="stats.completed > 0"
              color="success"
              variant="outlined"
              prepend-icon="mdi-check-circle"
              size="small"
            >
              Erledigt: {{ stats.completed }}
            </v-chip>
          </v-card-subtitle>
        </v-card>

        <!-- Tabs -->
        <v-card class="kv-list-card">
          <v-tabs
            v-model="activeTab"
            color="success"
            align-tabs="start"
            @update:model-value="loadKvConfirmations"
          >
            <v-tab value="active">
              <v-icon class="mr-2">mdi-file-document-edit</v-icon>
              Aktiv
            </v-tab>
            <v-tab value="archived">
              <v-icon class="mr-2">mdi-archive</v-icon>
              Archiviert
            </v-tab>
          </v-tabs>

          <v-divider />

          <!-- KV-Liste -->
          <v-card-text class="pa-0">
            <v-list v-if="kvList.length > 0" class="kv-list">
              <v-list-item
                v-for="kv in kvList"
                :key="kv.id"
                class="kv-item"
                :class="[getKvItemClass(kv), { 'mobile-kv-item': isMobile }]"
                @click="openKvDetail(kv)"
              >
                <template #prepend>
                  <v-avatar
                    :color="getStatusColor(kv.status)"
                    :size="isMobile ? 40 : 50"
                  >
                    <v-icon :size="isMobile ? 22 : 30">{{ getStatusIcon(kv.status) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title class="kv-title-row">
                  <span class="font-weight-bold kv-nummer">KV {{ kv.kv_nummer }}</span>

                  <div class="kv-chips">
                    <v-chip
                      :color="getStatusColor(kv.status)"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="flat"
                    >
                      {{ getStatusText(kv.status) }}
                    </v-chip>

                    <v-chip
                      v-if="kv.is_completed"
                      color="success"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="outlined"
                      prepend-icon="mdi-check"
                    >
                      Abgeschlossen
                    </v-chip>
                  </div>
                </v-list-item-title>

                <v-list-item-subtitle class="mt-2">
                  <div class="kv-info-grid">
                    <div class="info-item">
                      <v-icon size="small" class="mr-1">mdi-account-wrench</v-icon>
                      <span :class="{ 'small-text': isMobile }">
                        {{ isMobile ? 'M:' : 'Monteur:' }}
                        {{ kv.monteur_vorname || 'N/A' }} {{ kv.monteur_nachname || '' }}
                        <span v-if="kv.monteur_nummer">({{ kv.monteur_nummer }})</span>
                      </span>
                    </div>

                    <div class="info-item">
                      <v-icon size="small" class="mr-1">mdi-account-edit</v-icon>
                      <span :class="{ 'small-text': isMobile }">
                        {{ isMobile ? 'B:' : 'Bearbeiter:' }}
                        {{ kv.bearbeiter_vorname || 'N/A' }} {{ kv.bearbeiter_nachname || '' }}
                        <span v-if="kv.bearbeiter_nummer">({{ kv.bearbeiter_nummer }})</span>
                      </span>
                    </div>

                    <div class="info-item">
                      <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                      <span :class="{ 'small-text': isMobile }">{{ formatDate(kv.created_at) }}</span>
                    </div>
                  </div>

                  <div v-if="kv.bearbeiter_notiz" class="mt-2 notiz-preview">
                    <v-icon size="small" class="mr-1">mdi-note-text</v-icon>
                    {{ truncateText(kv.bearbeiter_notiz, isMobile ? 50 : 80) }}
                  </div>
                </v-list-item-subtitle>

                <template #append>
                  <div :class="isMobile ? 'mobile-actions' : 'd-flex flex-column align-end gap-2'">
                    <v-btn
                      v-if="!kv.is_completed && isMonteur"
                      icon="mdi-check-circle"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="text"
                      color="success"
                      @click.stop="openCompleteDialog(kv)"
                    />

                    <v-btn
                      v-if="!kv.is_archived && !kv.is_completed && canCreateKv"
                      icon="mdi-delete"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="text"
                      color="error"
                      @click.stop="deleteKv(kv)"
                    />

                    <v-btn
                      v-if="!kv.is_archived && canArchiveKv"
                      icon="mdi-archive"
                      :size="isMobile ? 'x-small' : 'small'"
                      variant="text"
                      color="grey"
                      @click.stop="archiveKv(kv)"
                    />
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <!-- Leerer Zustand -->
            <div v-else class="empty-state pa-8 text-center">
              <v-icon size="80" color="grey-lighten-1">mdi-file-document-off</v-icon>
              <h3 class="text-h6 mt-4 mb-2">Keine KV-Bestätigungen</h3>
              <p class="text-body-2 text-medium-emphasis">
                {{ activeTab === 'active' ? 'Keine aktiven KV-Bestätigungen vorhanden' : 'Keine archivierten KV-Bestätigungen' }}
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Neue KV-Bestätigung Dialog -->
    <v-dialog v-model="showNewKvDialog" max-width="600">
      <v-card class="new-kv-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="success" size="28" class="mr-3">mdi-file-document-plus</v-icon>
            <span class="text-h6">Neue KV-Bestätigung</span>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeNewKvDialog" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <v-form ref="newKvForm" @submit.prevent="createKvConfirmation">
            <!-- KV-Nummer -->
            <v-text-field
              v-model="newKv.kv_nummer"
              label="KV-Nummer (7-10 Stellen)"
              prepend-inner-icon="mdi-pound"
              variant="outlined"
              type="text"
              inputmode="numeric"
              :rules="[
                v => !!v || 'KV-Nummer erforderlich',
                v => /^\d+$/.test(v) || 'Nur Zahlen erlaubt',
                v => (v && v.length >= 7 && v.length <= 10) || 'Muss 7-10 Ziffern lang sein'
              ]"
              @input="newKv.kv_nummer = newKv.kv_nummer.replace(/\D/g, '')"
              class="mb-4"
            />

            <!-- Monteur auswählen -->
            <v-autocomplete
              v-model="newKv.mitarbeiter_id"
              :items="availableMonteure"
              item-title="name"
              item-value="id"
              label="Monteur zuweisen"
              prepend-inner-icon="mdi-account-wrench"
              variant="outlined"
              :rules="[v => !!v || 'Monteur erforderlich']"
              :custom-filter="filterMonteure"
              class="mb-4"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item
                  v-bind="props"
                  :subtitle="item.raw.mitarbeiternummer"
                />
              </template>
            </v-autocomplete>

            <!-- Status -->
            <v-select
              v-model="newKv.status"
              :items="statusOptions"
              label="Status"
              prepend-inner-icon="mdi-flag"
              variant="outlined"
              :rules="[v => !!v || 'Status erforderlich']"
              class="mb-4"
            >
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
                  </template>
                </v-list-item>
              </template>

              <template #selection="{ item }">
                <div class="d-flex align-center">
                  <v-icon :color="item.raw.color" class="mr-2">{{ item.raw.icon }}</v-icon>
                  {{ item.title }}
                </div>
              </template>
            </v-select>

            <!-- Notiz -->
            <v-textarea
              v-model="newKv.bearbeiter_notiz"
              label="Notiz (optional)"
              prepend-inner-icon="mdi-note-text"
              variant="outlined"
              rows="3"
              auto-grow
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey"
            variant="text"
            @click="closeNewKvDialog"
            :disabled="creating"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            prepend-icon="mdi-check"
            @click="createKvConfirmation"
            :loading="creating"
          >
            Erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- KV Abschließen Dialog (für Monteure) -->
    <v-dialog v-model="showCompleteDialog" max-width="600">
      <v-card v-if="selectedKv" class="complete-kv-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="success" size="28" class="mr-3">mdi-check-circle</v-icon>
            <span class="text-h6">KV {{ selectedKv.kv_nummer }} abschließen</span>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeCompleteDialog" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <!-- KV Info -->
          <v-alert
            :color="getStatusColor(selectedKv.status)"
            variant="tonal"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2">{{ getStatusIcon(selectedKv.status) }}</v-icon>
              <div>
                <div class="font-weight-bold">Status: {{ getStatusText(selectedKv.status) }}</div>
                <div class="text-caption">Bearbeiter: {{ selectedKv.bearbeiter_vorname }} {{ selectedKv.bearbeiter_nachname }}</div>
              </div>
            </div>
          </v-alert>

          <!-- Bearbeiter-Notiz anzeigen -->
          <v-alert
            v-if="selectedKv.bearbeiter_notiz"
            color="info"
            variant="tonal"
            class="mb-4"
          >
            <div class="text-caption mb-1">Notiz vom Bearbeiter:</div>
            <div>{{ selectedKv.bearbeiter_notiz }}</div>
          </v-alert>

          <!-- Monteur-Notiz -->
          <v-textarea
            v-model="completeForm.monteur_notiz"
            label="Ihre Notiz (optional)"
            prepend-inner-icon="mdi-note-text"
            variant="outlined"
            rows="3"
            auto-grow
            class="mb-4"
          />

          <!-- In Instandsetzung eintragen -->
          <v-checkbox
            v-model="completeForm.add_to_instandsetzung"
            label="Als abgeschlossen in Instandsetzung eintragen"
            color="success"
            class="mb-2"
          >
            <template v-slot:append>
              <v-tooltip text="Erstellt automatisch einen Auftrag in Ihren Instandsetzungen" location="top">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" size="small" color="info">mdi-information</v-icon>
                </template>
              </v-tooltip>
            </template>
          </v-checkbox>

          <v-alert
            v-if="completeForm.add_to_instandsetzung"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4 text-caption"
          >
            Bitte wählen Sie den Gerätetyp und den Mitarbeiter, der die Instandsetzung durchführen soll (Sie selbst oder ein Kollege).
          </v-alert>

          <!-- Gerätetyp-Auswahl (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="completeForm.add_to_instandsetzung"
            v-model="completeForm.geraetetyp_id"
            :items="availableGeraetetypen"
            item-title="name"
            item-value="id"
            label="Gerätetyp auswählen *"
            prepend-inner-icon="mdi-washing-machine"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Gerätetyp auswählen']"
            required
            class="mb-4"
          />

          <!-- Reparaturtyp-Auswahl (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="completeForm.add_to_instandsetzung"
            v-model="completeForm.reparaturtyp_id"
            :items="availableReparaturtypen"
            item-title="name"
            item-value="id"
            label="Reparaturtyp auswählen *"
            prepend-inner-icon="mdi-wrench"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Reparaturtyp auswählen']"
            required
            class="mb-4"
          />

          <!-- Mitarbeiter-Auswahl für Instandsetzung (nur wenn add_to_instandsetzung aktiviert) -->
          <v-select
            v-if="completeForm.add_to_instandsetzung"
            v-model="completeForm.instandsetzung_mitarbeiter_id"
            :items="availableMonteure"
            item-title="name"
            item-value="id"
            label="Mitarbeiter für Instandsetzung auswählen *"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            :rules="[v => !!v || 'Bitte Mitarbeiter auswählen']"
            required
          >
            <template v-slot:item="{ props, item }">
              <v-list-item
                v-bind="props"
                :subtitle="item.raw.mitarbeiternummer"
              />
            </template>
          </v-select>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey"
            variant="text"
            @click="closeCompleteDialog"
            :disabled="completing"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            prepend-icon="mdi-check-circle"
            @click="completeKv"
            :loading="completing"
          >
            Abschließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- KV Detail Dialog -->
    <v-dialog v-model="showDetailDialog" max-width="700">
      <v-card v-if="selectedKv" class="detail-card">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar :color="getStatusColor(selectedKv.status)" size="40" class="mr-3">
              <v-icon>{{ getStatusIcon(selectedKv.status) }}</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6">KV {{ selectedKv.kv_nummer }}</div>
              <div class="text-caption">{{ getStatusText(selectedKv.status) }}</div>
            </div>
          </div>

          <v-btn icon="mdi-close" variant="text" @click="closeDetailDialog" />
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <!-- Status Info -->
          <v-row>
            <v-col cols="6">
              <div class="info-label">Status</div>
              <v-chip :color="getStatusColor(selectedKv.status)" variant="flat">
                {{ getStatusText(selectedKv.status) }}
              </v-chip>
            </v-col>

            <v-col cols="6">
              <div class="info-label">Abgeschlossen</div>
              <v-chip :color="selectedKv.is_completed ? 'success' : 'grey'" variant="outlined">
                {{ selectedKv.is_completed ? 'Ja' : 'Nein' }}
              </v-chip>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Personen Info -->
          <v-row>
            <v-col cols="6">
              <div class="info-label">Monteur</div>
              <div class="info-value">
                {{ selectedKv.monteur_vorname }} {{ selectedKv.monteur_nachname }}
                <div class="text-caption">Nr. {{ selectedKv.monteur_nummer }}</div>
              </div>
            </v-col>

            <v-col cols="6">
              <div class="info-label">Bearbeiter</div>
              <div class="info-value">
                {{ selectedKv.bearbeiter_vorname }} {{ selectedKv.bearbeiter_nachname }}
                <div class="text-caption">Nr. {{ selectedKv.bearbeiter_nummer }}</div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Zeitinformationen -->
          <div class="info-label">Erstellt am</div>
          <div class="info-value mb-3">{{ formatDateFull(selectedKv.created_at) }}</div>

          <div v-if="selectedKv.completed_at" class="info-label">Abgeschlossen am</div>
          <div v-if="selectedKv.completed_at" class="info-value mb-3">{{ formatDateFull(selectedKv.completed_at) }}</div>

          <v-divider class="my-4" />

          <!-- Notizen -->
          <div v-if="selectedKv.bearbeiter_notiz">
            <div class="info-label">Notiz vom Bearbeiter</div>
            <v-alert color="info" variant="tonal" class="mt-2 mb-4">
              {{ selectedKv.bearbeiter_notiz }}
            </v-alert>
          </div>

          <div v-if="selectedKv.monteur_notiz">
            <div class="info-label">Notiz vom Monteur</div>
            <v-alert color="success" variant="tonal" class="mt-2">
              {{ selectedKv.monteur_notiz }}
            </v-alert>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            v-if="!selectedKv.is_completed && isMonteur"
            color="success"
            variant="elevated"
            prepend-icon="mdi-check-circle"
            @click="openCompleteDialog(selectedKv); showDetailDialog = false"
          >
            Jetzt abschließen
          </v-btn>

          <v-btn
            color="grey"
            variant="text"
            @click="closeDetailDialog"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/auth';

const toast = useToast();
const authStore = useAuthStore();

// State
const loading = ref(false);
const creating = ref(false);
const completing = ref(false);
const activeTab = ref('active');
const kvList = ref([]);
const selectedKv = ref(null);
const showNewKvDialog = ref(false);
const showCompleteDialog = ref(false);
const showDetailDialog = ref(false);
const availableMonteure = ref([]);
const availableGeraetetypen = ref([]);
const availableReparaturtypen = ref([]);

// Responsive Design
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

const stats = ref({
  total: 0,
  pending: 0,
  completed: 0,
  reparieren: 0,
  warten_et: 0,
  unrepariert: 0
});

const newKv = ref({
  kv_nummer: '',
  mitarbeiter_id: null,
  status: 'reparieren',
  bearbeiter_notiz: ''
});

const completeForm = ref({
  monteur_notiz: '',
  add_to_instandsetzung: false,
  instandsetzung_mitarbeiter_id: null,
  geraetetyp_id: null,
  reparaturtyp_id: null
});

const statusOptions = [
  {
    title: 'Reparieren',
    value: 'reparieren',
    color: 'success',
    icon: 'mdi-wrench'
  },
  {
    title: 'Warten auf Ersatzteil',
    value: 'warten_et',
    color: 'warning',
    icon: 'mdi-clock-alert'
  },
  {
    title: 'Unrepariert zurück',
    value: 'unrepariert',
    color: 'error',
    icon: 'mdi-close-circle'
  }
];

// Computed
const isMonteur = computed(() => {
  return authStore.user?.monteur || false;
});

// Prüfe, ob der Benutzer KV-Bestätigungen erstellen darf
// Admins, Super-Admins, Entwickler und Lageristen dürfen KVs erstellen
const canCreateKv = computed(() => {
  return authStore.user?.admin ||
         authStore.user?.super_admin ||
         authStore.user?.developer ||
         authStore.user?.lagerist ||
         false;
});

// Computed: Kann Benutzer KVs archivieren? (nur Bearbeiter/Admins, nicht Monteure)
const canArchiveKv = computed(() => {
  return authStore.user?.admin ||
         authStore.user?.super_admin ||
         authStore.user?.developer ||
         authStore.user?.lagerist ||
         false;
});

// Computed: Ist Benutzer Admin oder Super-Admin?
const isAdminOrSuperAdmin = computed(() => {
  return authStore.user?.admin ||
         authStore.user?.super_admin ||
         authStore.user?.developer ||
         false;
});

// Window resize handler
function updateWidth() {
  windowWidth.value = window.innerWidth;
}

// Lifecycle
onMounted(() => {
  loadKvConfirmations();
  loadStats();
  loadAvailableMonteure();
  loadAvailableGeraetetypen();
  loadAvailableReparaturtypen();

  // WebSocket-Listener
  window.addEventListener('websocket-message', handleWebSocketMessage);

  // Window resize listener
  window.addEventListener('resize', updateWidth);
});

// Cleanup bei Component Unmount
onUnmounted(() => {
  window.removeEventListener('websocket-message', handleWebSocketMessage);
  window.removeEventListener('resize', updateWidth);
});

// Methods
async function loadKvConfirmations() {
  loading.value = true;

  try {
    const action = activeTab.value === 'active' ? 'list' : 'archived';
    const params = { action };

    // Admins/Super-Admins/Entwickler sehen alle KVs
    if (isAdminOrSuperAdmin.value) {
      params.show_all = true;
    }

    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params,
      withCredentials: true
    });

    if (response.data.status === 'success') {
      kvList.value = response.data.data || [];

      // Alle geladenen KVs als "gesehen" markieren (für alle Monteure)
      if (authStore.user?.monteur) {
        kvList.value.forEach(kv => {
          if (kv.id) {
            window.dispatchEvent(new CustomEvent('mark-kv-as-seen', {
              detail: { kvId: kv.id }
            }));
          }
        });
      }
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der KV-Bestätigungen');
    }
  } catch (error) {
    console.error('Fehler beim Laden der KV-Bestätigungen:', error);
    toast.error('Fehler beim Laden der KV-Bestätigungen');
  } finally {
    loading.value = false;
  }
}

async function loadStats() {
  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: { action: 'stats' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      stats.value = response.data.data;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Statistiken:', error);
  }
}

async function loadAvailableMonteure() {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/users.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      // Filtere nur Monteure und erstelle das richtige Format
      availableMonteure.value = response.data.users
        .filter(user => user.monteur)
        .map(user => ({
          id: user.id,
          name: `${user.vorname} ${user.nachname}`,
          mitarbeiternummer: user.mitarbeiternummer,
          vorname: user.vorname,
          nachname: user.nachname
        }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Monteure:', error);
  }
}

// Custom Filter für Monteur-Suche: Durchsucht Name UND Mitarbeiternummer
function filterMonteure(value, query, item) {
  if (!query) return true;

  const searchQuery = query.toLowerCase();
  const name = item.raw.name?.toLowerCase() || '';
  const nummer = item.raw.mitarbeiternummer?.toLowerCase() || '';

  return name.includes(searchQuery) || nummer.includes(searchQuery);
}

async function loadAvailableGeraetetypen() {
  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: { action: 'geraetetypen' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableGeraetetypen.value = response.data.data.map(typ => ({
        id: typ.id,
        name: typ.name
      }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Gerätetypen:', error);
  }
}

async function loadAvailableReparaturtypen() {
  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: { action: 'reparaturtypen' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      availableReparaturtypen.value = response.data.data.map(typ => ({
        id: typ.id,
        name: typ.name
      }));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Reparaturtypen:', error);
  }
}

async function createKvConfirmation() {
  creating.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}kv_confirmations.php`,
      newKv.value,
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('KV-Bestätigung erfolgreich erstellt');
      closeNewKvDialog();
      loadKvConfirmations();
      loadStats();
    } else {
      toast.error(response.data.message || 'Fehler beim Erstellen');
    }
  } catch (error) {
    console.error('Fehler beim Erstellen:', error);
    toast.error('Fehler beim Erstellen der KV-Bestätigung');
  } finally {
    creating.value = false;
  }
}

async function completeKv() {
  if (!selectedKv.value) return;

  // Validierung: Wenn add_to_instandsetzung aktiviert, müssen Gerätetyp, Reparaturtyp und Mitarbeiter ausgewählt sein
  if (completeForm.value.add_to_instandsetzung) {
    if (!completeForm.value.geraetetyp_id) {
      toast.error('Bitte wählen Sie einen Gerätetyp aus');
      return;
    }
    if (!completeForm.value.reparaturtyp_id) {
      toast.error('Bitte wählen Sie einen Reparaturtyp aus');
      return;
    }
    if (!completeForm.value.instandsetzung_mitarbeiter_id) {
      toast.error('Bitte wählen Sie einen Mitarbeiter für die Instandsetzung aus');
      return;
    }
  }

  completing.value = true;

  try {
    const response = await axios.put(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        action: 'complete',
        id: selectedKv.value.id,
        ...completeForm.value
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('KV erfolgreich abgeschlossen');
      closeCompleteDialog();
      loadKvConfirmations();
      loadStats();
    } else {
      toast.error(response.data.message || 'Fehler beim Abschließen');
    }
  } catch (error) {
    console.error('Fehler beim Abschließen:', error);
    toast.error('Fehler beim Abschließen der KV');
  } finally {
    completing.value = false;
  }
}

async function archiveKv(kv) {
  if (!confirm(`Möchten Sie KV ${kv.kv_nummer} wirklich archivieren?`)) {
    return;
  }

  try {
    const response = await axios.put(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        action: 'archive',
        id: kv.id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('KV archiviert');
      loadKvConfirmations();
      loadStats();
    }
  } catch (error) {
    console.error('Fehler beim Archivieren:', error);
    toast.error('Fehler beim Archivieren der KV');
  }
}

async function deleteKv(kv) {
  if (!confirm(`Möchten Sie KV ${kv.kv_nummer} wirklich löschen?`)) {
    return;
  }

  try {
    const response = await axios.delete(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        data: { id: kv.id },
        withCredentials: true
      }
    );

    if (response.data.status === 'success') {
      toast.success('KV gelöscht');
      loadKvConfirmations();
      loadStats();
    }
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
    toast.error('Fehler beim Löschen der KV');
  }
}

function openKvDetail(kv) {
  selectedKv.value = kv;
  showDetailDialog.value = true;
}

function openCompleteDialog(kv) {
  selectedKv.value = kv;
  showCompleteDialog.value = true;
  // Vorauswahl: Wenn Monteur, wähle sich selbst für Instandsetzung vor
  if (isMonteur.value && authStore.user) {
    completeForm.value.instandsetzung_mitarbeiter_id = authStore.user.id;
  }
}

function closeNewKvDialog() {
  showNewKvDialog.value = false;
  newKv.value = {
    kv_nummer: '',
    mitarbeiter_id: null,
    status: 'reparieren',
    bearbeiter_notiz: ''
  };
}

function closeCompleteDialog() {
  showCompleteDialog.value = false;
  selectedKv.value = null;
  completeForm.value = {
    monteur_notiz: '',
    add_to_instandsetzung: false,
    instandsetzung_mitarbeiter_id: null,
    geraetetyp_id: null,
    reparaturtyp_id: null
  };
}

function closeDetailDialog() {
  showDetailDialog.value = false;
  selectedKv.value = null;
}

function handleWebSocketMessage(event) {
  const data = event.detail;

  if (data.type === 'new_kv_confirmation' || data.type === 'kv_completed' ||
      data.type === 'kv_archived' || data.type === 'kv_deleted' ||
      data.type === 'kv_takeover') {
    // KV-Bestätigung empfangen, abgeschlossen, archiviert, gelöscht oder übernommen - aktualisieren
    loadKvConfirmations();
    loadStats();

    // KEINE Toasts in KvConfirmationView
    // Die Toasts werden zentral in MessageCenterView gezeigt
    // Hier wird nur die Liste aktualisiert
  }
}

// Utility Functions
function getStatusColor(status) {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.color : 'grey';
}

function getStatusIcon(status) {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.icon : 'mdi-file-document';
}

function getStatusText(status) {
  const option = statusOptions.find(opt => opt.value === status);
  return option ? option.title : 'Unbekannt';
}

function getKvItemClass(kv) {
  const classes = [];

  if (!kv.is_completed) {
    classes.push('kv-item-pending');
  }

  if (kv.status === 'reparieren') {
    classes.push('kv-item-reparieren');
  } else if (kv.status === 'warten_et') {
    classes.push('kv-item-warten');
  } else if (kv.status === 'unrepariert') {
    classes.push('kv-item-unrepariert');
  }

  return classes.join(' ');
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;

  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `vor ${minutes} Min.`;
  }

  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `vor ${hours} Std.`;
  }

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function truncateText(text, maxLength) {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}
</script>

<style scoped>
.kv-confirmation-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.header-card,
.kv-list-card,
.new-kv-card,
.complete-kv-card,
.detail-card {
  background: rgba(30, 30, 35, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.title-text {
  font-size: 1.5rem;
  font-weight: 600;
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-chips {
  display: flex;
  gap: 8px;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.stats-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.kv-list {
  max-height: 70vh;
  overflow-y: auto;
}

.kv-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
}

.kv-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.kv-item-pending {
  border-left: 3px solid rgba(255, 193, 7, 0.5);
}

.kv-item-reparieren {
  background: rgba(76, 175, 80, 0.03);
}

.kv-item-warten {
  background: rgba(255, 193, 7, 0.03);
}

.kv-item-unrepariert {
  background: rgba(244, 67, 54, 0.03);
}

.notiz-preview {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  font-style: italic;
}

.empty-state {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.info-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 8px;
}

.info-value {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

.kv-info-grid {
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

/* ======== Mobile Optimierungen ======== */
@media (max-width: 959px) {
  .kv-confirmation-container {
    padding: 12px;
  }

  .header-card {
    margin-bottom: 12px !important;
  }

  .header-title {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
    padding: 12px !important;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    width: 100%;
    justify-content: flex-end;
  }

  .stats-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding-top: 0 !important;
  }

  .title-text {
    font-size: 1.25rem;
  }

  .kv-list {
    max-height: calc(100vh - 280px);
  }

  .mobile-kv-item {
    padding: 12px 8px !important;
  }

  .kv-title-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start !important;
  }

  .kv-nummer {
    font-size: 0.95rem;
  }

  .kv-chips {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  .kv-info-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .info-item {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
  }

  .small-text {
    font-size: 0.8rem;
  }

  .notiz-preview {
    font-size: 0.75rem;
  }

  .mobile-actions {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
  }

  .empty-state {
    min-height: 300px;
    padding: 16px !important;
  }

  .empty-state .v-icon {
    font-size: 60px !important;
  }

  .empty-state h3 {
    font-size: 1.1rem !important;
  }

  .empty-state p {
    font-size: 0.875rem !important;
  }
}

@media (max-width: 600px) {
  .kv-confirmation-container {
    padding: 8px;
  }

  .header-card {
    border-radius: 8px;
  }

  .stats-row .v-chip {
    font-size: 0.7rem;
  }

  .kv-list-card {
    border-radius: 8px;
  }

  .mobile-kv-item {
    padding: 10px 6px !important;
  }

  .info-item {
    font-size: 0.75rem;
  }

  .small-text {
    font-size: 0.7rem;
  }
}
</style>
